import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  ApolloOnError,
  RetryLink,
} from '@common/hooks';
import { createUploadLink } from 'apollo-upload-client';
import auth from 'works/legacy/lib/auth';
import { redirectToLogin } from 'works/legacy/lib/redirect-to-login';
import Filters from 'works/legacy/lib/filters';
import { message as toastMessage } from 'antd';
import throttle from 'lodash/throttle';
import { lastRequestTimeMiddleware } from '@identity/graphql/middlewares/last-request-time-middleware';

const csrfMiddleware = new ApolloLink((operation, forward) => {
  // won't exist in rails test env
  const csrfMeta = document.querySelector('meta[name=csrf-token]');
  const csrfToken = csrfMeta ? csrfMeta.getAttribute('content') : '';
  const identityToken = auth.identityAuthToken();
  const email = auth.email();
  const membershipId = auth.membershipId();

  operation.setContext({
    headers: {
      ...operation.getContext().headers,
      'X-CSRF-Token': csrfToken,
      'X-WORKS-MEMBERSHIP-ID': membershipId,
      'X-IDENTITY--USER-TOKEN': identityToken,
      'X-IDENTITY--USER-EMAIL': email,
    },
  });

  return forward(operation);
});

// Prevent this function being called repeatedly in a short time
// as we can have many requests with an unauthenticated error.
const redirectToLoginOnce = throttle(redirectToLogin, 1000, { leading: true });

const errorHandler = ApolloOnError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`
      );

      if (extensions?.code === 'UNAUTHENTICATED') {
        redirectToLoginOnce();
      }

      if (message.startsWith('Variable $filters of type')) {
        Filters.clearFilters();
        toastMessage.warn(
          'The filters have been removed due to being invalid or outdated.'
        );
      }
    });
  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
  }
});

const httpLink = createUploadLink({
  uri: '/graphql',
  credentials: 'same-origin',
});

const cache = new InMemoryCache();

const operationIsReadOnly = (error, operation) => {
  return operation?.query?.definitions?.every(
    (d) => d?.operation !== 'mutation'
  );
};

const retryLink = new RetryLink({
  delay: {
    initial: 100,
    max: 3000,
    jitter: true,
  },
  attempts: {
    max: 10,
    retryIf: operationIsReadOnly,
  },
});

const graphQl = new ApolloClient({
  cache,
  link: ApolloLink.from([
    errorHandler,
    retryLink,
    lastRequestTimeMiddleware,
    csrfMiddleware,
    httpLink,
  ]),
  resolvers: {},
});

graphQl.defaultOptions = {
  mutate: {
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  },
  query: {
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  },
  watchQuery: {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  },
};

export default graphQl;
